/**
 * Набор языков домена, запомненный на этом origin.
 *
 * Язык кабинета решает ДОМЕН (`GET /api/v1/cabinet/site`), и до ответа ручки
 * говорить не на чем. Раньше в это окно кабинет говорил на запасном русском —
 * и на английском домене (aquaculture.lv, замер владельца 12.08.2026) КАЖДАЯ
 * жёсткая перезагрузка показывала русский интерфейс, который затем на глазах
 * переписывался английским. localStorage у каждого origin свой, так что
 * прежний выбор языка с основного домена сюда не доезжает и прикрыть окно
 * нечем.
 *
 * Лечим двумя движениями:
 *   1. ответ ручки кладём рядом с выбором языка и на следующем заходе читаем
 *      СИНХРОННО, до первого кадра — вспышки нет вовсе;
 *   2. самый первый в жизни заход (кэша нет) ждёт ответ, а не рисует наугад.
 *      Ручка лёгкая и локальная; если она молчит дольше терпимого, рисуем на
 *      запасном языке — пустой экран из-за молчащего бэкенда недопустим.
 */
import { parseFrame, parsePeriod } from '../domain/limits'
import { resolveLang, type SiteBrand, type SiteLangs } from './resolveLang'
import { applyLang, navigatorLangs, storedLang } from './store'
import { isLang } from './types'

export const SITE_KEY = 'cabinet.site'

/** Сколько ждём первый ответ, прежде чем рисовать на запасном языке. */
export const SITE_WAIT_MS = 1500

/**
 * Разбор бренда домена: всё, что не похоже на пак, — `null`.
 *
 * Ходит по тому же рельсу, что и набор языков (ручка → память origin → первый
 * кадр), поэтому и разбирается тем же способом: витрина не верит ни ответу, ни
 * своей же записи в localStorage. Адрес знака отдельно проверяется на «свой
 * origin»: по этому адресу идёт `fetch`, а разметка ВКЛЕИВАЕТСЯ в документ
 * (см. `src/brand/brandMark.ts`), так что чужой хост здесь означал бы чужой
 * код в нашем окне. Правило то же, что у коротких ссылок `/s/` на бэкенде:
 * путь внутри своего сайта, без `//` и без схемы.
 */
export function parseBrand(raw: unknown): SiteBrand | null {
  if (!raw || typeof raw !== 'object') return null
  const body = raw as {
    id?: unknown
    name?: unknown
    names?: unknown
    logo?: unknown
    regions?: unknown
    palette_code?: unknown
    map?: unknown
    basemap?: unknown
    national_flag?: unknown
    default_species?: unknown
    frame?: unknown
    period?: unknown
  }
  const logo = (body.logo ?? {}) as { url?: unknown; mono?: unknown }
  if (typeof body.id !== 'string' || !body.id) return null
  if (typeof body.name !== 'string' || !body.name) return null
  if (typeof logo.url !== 'string' || !isOwnOriginPath(logo.url)) return null
  return {
    id: body.id,
    name: body.name,
    names: parseNames(body.names),
    // Умолчание — моно: так же считает загрузчик пака (`logo.mono` по умолчанию
    // true). Пак без поля не должен рисоваться иначе, чем на бэкенде.
    logo: { url: logo.url, mono: logo.mono !== false },
    regions: Array.isArray(body.regions) ? body.regions.filter(r => typeof r === 'string') : [],
    // Код палитры и цвета линий — по тому же правилу, что имена по языкам:
    // мусор выбрасывается по частям, а не гасит бренд целиком.
    palette_code: typeof body.palette_code === 'string' && body.palette_code
      ? body.palette_code
      : null,
    map: parseMapColors(body.map),
    basemap: parseBasemap(body.basemap),
    national_flag: typeof body.national_flag === 'string' && /^[A-Z]{3}$/.test(body.national_flag)
      ? body.national_flag
      : null,
    default_species: typeof body.default_species === 'number'
      && Number.isInteger(body.default_species) && body.default_species > 0
      ? body.default_species
      : null,
    // Пределы домена — по частям, как подложка: битое поле значит «не
    // ограничено» на витрине, а не потерю бренда; сервер держит своё.
    frame: parseFrame(body.frame),
    period: parsePeriod(body.period),
  }
}

/** Цвета линий карты: `{isotherm: [15,42,38], drift: [[…],[…]]}`.
 *
 *  Негодный ключ выбрасывается поштучно — как и имя на чужом языке: мусор в
 *  одном цвете не должен отнимать у карты остальные. */
/**
 * Подложка домена. Всё, что не пара «поставщик [+ ключ]», — `null`.
 *
 * Разбирается ПО ЧАСТЯМ, как `palette_code` и цвета линий: битая подложка не
 * гасит бренд целиком — домен остаётся со своим именем, знаком и палитрой, а
 * карту рисует прежним Carto. Поставщик здесь НЕ сверяется со списком
 * известных: это делает `basemapFrom`, и делает единственным способом —
 * откатом на Carto. Вторая проверка тут означала бы второе место, где список
 * поставщиков может отстать.
 */
function parseBasemap(raw: unknown): { provider: string; apikey?: string } | null {
  if (!raw || typeof raw !== 'object') return null
  const body = raw as { provider?: unknown; apikey?: unknown }
  if (typeof body.provider !== 'string' || !body.provider) return null
  return typeof body.apikey === 'string' && body.apikey
    ? { provider: body.provider, apikey: body.apikey }
    : { provider: body.provider }
}

function parseMapColors(raw: unknown): Record<string, number[] | number[][]> {
  if (!raw || typeof raw !== 'object') return {}
  const isRgb = (v: unknown): v is number[] =>
    Array.isArray(v) && v.length === 3 && v.every(n => typeof n === 'number' && n >= 0 && n <= 255)
  const out: Record<string, number[] | number[][]> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isRgb(value)) out[key] = value
    else if (Array.isArray(value) && value.length >= 2 && value.every(isRgb)) {
      out[key] = value as number[][]
    }
  }
  return out
}

/**
 * Имена бренда по языкам: `{"en": "Rumb"}`. Негодная пара выбрасывается.
 *
 * Выбрасывается пара, а не весь бренд: мусор в одном языке не должен отнимать
 * у кабинета знак и имя на остальных — то же правило, по которому битый бренд
 * гасит бренд, а не набор языков. Чужой язык (`de`) отсеивается здесь, потому
 * что каталога под него в кабинете нет: показать имя было бы некому.
 */
function parseNames(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, string> = {}
  for (const [lang, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isLang(lang) && typeof value === 'string' && value.trim()) out[lang] = value
  }
  return out
}

/** Путь внутри своего сайта: не схема, не `//host`, не обратная косая. */
function isOwnOriginPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')
}

export function serializeSite(site: SiteLangs): string {
  return JSON.stringify({
    languages: site.languages,
    default_lang: site.default_lang,
    // Кладём УЖЕ разобранный бренд: запомненное значение читается синхронно до
    // первого кадра, и разбирать его начисто на горячем пути незачем — а форма
    // всё равно проверяется на чтении, кэш ведь переживает выкладки витрины.
    brand: parseBrand(site.brand),
  })
}

/** Разбор запомненного значения; всё, что не похоже на набор, — null. */
export function parseCachedSite(raw: string | null | undefined): SiteLangs | null {
  if (!raw) return null
  try {
    const body = JSON.parse(raw) as Partial<SiteLangs>
    if (!Array.isArray(body?.languages) || typeof body?.default_lang !== 'string') return null
    // Битый бренд гасит бренд, а не весь набор: имя и знак у витрины запасные
    // есть, а вот потеря набора языков вернула бы вспышку русского кадра на
    // английском домене — то самое, ради чего этот кэш и заведён.
    return {
      languages: body.languages,
      default_lang: body.default_lang,
      brand: parseBrand(body.brand),
    }
  } catch {
    return null
  }
}

/** Держать ли первый кадр: набора нет и терпение ещё не вышло. */
export function holdFirstPaint(site: SiteLangs | null, waited: boolean): boolean {
  return site == null && !waited
}

export function readCachedSite(): SiteLangs | null {
  try {
    return parseCachedSite(localStorage.getItem(SITE_KEY))
  } catch {
    return null
  }
}

/**
 * Применить язык ДО первого кадра — по запомненному набору домена.
 *
 * Вызывается из main.tsx рядом с темой, а не эффектом провайдера: эффект
 * работает после первой отрисовки, поэтому одного кэша мало — кадр русского
 * успел бы показаться. Профиль вошедшего пользователя приезжает позже и
 * уточняет выбор внутри набора (см. LangProvider).
 */
export function initLang(): void {
  const site = readCachedSite()
  applyLang(
    resolveLang({
      site,
      profileLang: null,
      storedLang: storedLang(),
      browserLangs: navigatorLangs(),
    }),
  )
}

export function cacheSite(site: SiteLangs | null): void {
  if (!site) return
  try {
    localStorage.setItem(SITE_KEY, serializeSite(site))
  } catch {
    /* ignore */
  }
}
